import { useCallback } from 'react'
import { useConversionScopedStore } from '../../../store/scoped/useConversionScopedStore'
import { useUserStore } from '../../../store/useUserStore'
import { useAppPreferencesStore } from '../../../store/useAppPreferencesStore'
import { useGetOnRampPricePerUnit } from '../../../domain'
import { currencyUtils } from '../../../utils/currency'
import type { CryptoCode, FiatCode } from '../../../constants/currencyCodes'

const { cryptoToFiat, fiatToCrypto, parseAmountToFloat, cryptoToBig } =
  currencyUtils

type CryptoConversionInput = {
  value: string
  type: 'from' | 'to'
}

/**
 * `useCryptoConversion` is responsible for all the logic related to the conversion of the crypto currency.
 */
export const useCryptoConversion = () => {
  const setConversion = useConversionScopedStore((state) => state.setConversion)
  const { from, to } = useConversionScopedStore((state) => state.conversion)
  const isFiatMode = useConversionScopedStore((state) => state.isFiatMode)
  const setIsFiatMode = useConversionScopedStore((state) => state.setIsFiatMode)
  const setConversionPayload = useConversionScopedStore(
    (state) => state.setConversionPayload,
  )
  const displayCurrency = useAppPreferencesStore(
    (state) => state.displayCurrency,
  )

  // Получаем курсы обмена для обеих валют
  const { data: fromOnRampPricePerUnit, isLoading: isFromPriceLoading } =
    useGetOnRampPricePerUnit({
      crypto: from.currencyInfoInWallet?.code as CryptoCode,
      fiat: (displayCurrency?.isin as any) || 'USD',
    })

  const { data: toOnRampPricePerUnit, isLoading: isToPriceLoading } =
    useGetOnRampPricePerUnit({
      crypto: to.currencyInfoInWallet?.code as CryptoCode,
      fiat: (displayCurrency?.isin as any) || 'USD',
    })

  // Get fresh data from store every time
  const getConversionDataMap = () => {
    const currentState = useConversionScopedStore.getState().conversion
    return {
      from: currentState.from,
      to: currentState.to,
    }
  }

  /**
   * `updateConversionData` is responsible for updating the conversion data in zustand store.
   */
  const updateConversionData = ({
    type,
    fiatAmount,
    cryptoAmount,
    cryptoCode,
  }: {
    type: 'from' | 'to'
    fiatAmount: string
    cryptoAmount: string
    cryptoCode: CryptoCode
  }) => {
    const currentData = getConversionDataMap()
    const currentTypeData = currentData[type]

    const newData = {
      [type]: {
        ...currentTypeData,
        fiatAmount: {
          amount: fiatAmount,
          currency: (displayCurrency?.isin as FiatCode) || 'USD',
        },
        cryptoAmount: {
          amount: cryptoAmount,
          currency: cryptoCode,
        },
      },
    }

    setConversion(newData)
  }

  /**
   * `handleResetInverseValue` resets the inverse input to zero when user types.
   * The actual conversion will be calculated by the API.
   */
  const handleResetInverseValue = ({ type }: { type: 'from' | 'to' }) => {
    const isFrom = type === 'from'
    const inverseType = isFrom ? 'to' : 'from'

    const currentData = getConversionDataMap()
    const inverseTypeData = currentData[inverseType]

    updateConversionData({
      type: inverseType,
      fiatAmount: '0',
      cryptoAmount: '0',
      cryptoCode: inverseTypeData.cryptoAmount.currency,
    })
  }

  /**
   * `handleInputChange` is responsible for handling the value change of the focused input field.
   * It only updates the input being edited - the inverse value will be calculated by the API.
   */
  const handleInputChange = useCallback(
    ({ type, value = '0' }: CryptoConversionInput) => {
      const parsedAmount = parseAmountToFloat({
        amount: value,
        decimalSeparator: '.',
      })

      const bigAmount = cryptoToBig({
        amount: value,
      })

      const selectedTypeData = getConversionDataMap()[type]

      // Получаем курс обмена между криптовалютой и выбранной валютой
      let exchangeRate =
        Number(
          selectedTypeData.currencyInfoInWallet?.code ===
            from.currencyInfoInWallet?.code
            ? fromOnRampPricePerUnit?.amount
            : toOnRampPricePerUnit?.amount,
        ) ||
        selectedTypeData.currencyInfoInWallet?.pricePerUnit ||
        0

      // XAUT теперь поддерживается API напрямую

      const equivalentInFiat = cryptoToFiat({
        amountCrypto: parsedAmount,
        cryptoPricePerUnit: exchangeRate,
      })

      updateConversionData({
        type,
        fiatAmount: equivalentInFiat,
        cryptoAmount: bigAmount,
        cryptoCode:
          (selectedTypeData.currencyInfoInWallet?.code as CryptoCode) || 'USDT',
      })

      // Reset the inverse field to 0 - it will be updated by API response
      handleResetInverseValue({ type })
    },
    [
      fromOnRampPricePerUnit,
      toOnRampPricePerUnit,
      displayCurrency,
      from.currencyInfoInWallet?.code,
      to.currencyInfoInWallet?.code,
    ],
  )

  /**
   * `handleInputChangeInFiatMode` is responsible for handling the value change of the focused input field in fiat mode.
   * It only updates the input being edited - the inverse value will be calculated by the API.
   */
  const handleInputChangeInFiatMode = useCallback(
    ({ type, value = '0' }: CryptoConversionInput) => {
      const parsedAmount = parseAmountToFloat({
        amount: value,
        decimalSeparator: '.',
      })

      const currentData = getConversionDataMap()
      const selectedTypeData = currentData[type]

      // Получаем курс обмена между криптовалютой и выбранной валютой
      let exchangeRate =
        Number(
          selectedTypeData.currencyInfoInWallet?.code ===
            from.currencyInfoInWallet?.code
            ? fromOnRampPricePerUnit?.amount
            : toOnRampPricePerUnit?.amount,
        ) ||
        selectedTypeData.currencyInfoInWallet?.pricePerUnit ||
        0

      // XAUT теперь поддерживается API напрямую

      // Convert input fiat to crypto
      const equivalentInCrypto = fiatToCrypto({
        relativeFiatAmount: parsedAmount,
        cryptoPricePerUnitInFiat: exchangeRate,
      })

      // Update input field data
      updateConversionData({
        type,
        fiatAmount: value,
        cryptoAmount: equivalentInCrypto,
        cryptoCode:
          (selectedTypeData.currencyInfoInWallet?.code as CryptoCode) || 'USDT',
      })

      // Reset the inverse field to 0 - it will be updated by API response
      handleResetInverseValue({ type })
    },
    [
      fromOnRampPricePerUnit,
      toOnRampPricePerUnit,
      displayCurrency,
      from.currencyInfoInWallet?.code,
      to.currencyInfoInWallet?.code,
    ],
  )

  /**
   * `handleSwapCurrencies` is responsible for swapping the values of the selected currency with the inverse currency.
   */
  const handleSwapCurrencies = () => {
    // Simply swap currencies without clearing values
    setConversion({
      from: to,
      to: from,
    })
  }

  const handleSelectCryptoFromBottomSheet = (
    type: 'from' | 'to',
    cryptoCurrencyCode: CryptoCode,
  ) => {
    // Get fresh data from user store
    const userWallet = useUserStore.getState().wallet
    const completeCurrencies = userWallet?.completeCurrencies || []

    const selectedWallet = completeCurrencies.find(
      (currency) => currency.code === cryptoCurrencyCode,
    )

    if (!selectedWallet) {
      return
    }

    // Get input refs from store to clear input values
    const inputController = useConversionScopedStore.getState().inputController
    const debitInputRef = inputController.debitInputRef
    const creditInputRef = inputController.creditInputRef

    // Clear both input values when any currency changes
    if (debitInputRef?.current) {
      debitInputRef.current.value = '0'
    }
    if (creditInputRef?.current) {
      creditInputRef.current.value = '0'
    }

    // Get current conversion state to reset both currencies
    const currentConversion = useConversionScopedStore.getState().conversion

    // Use actual wallet data and reset both currencies to 0
    setConversion({
      from: {
        fiatAmount: {
          amount: '0',
          currency: (displayCurrency?.isin as FiatCode) || 'USD',
        },
        cryptoAmount: {
          amount: '0',
          currency:
            type === 'from'
              ? cryptoCurrencyCode
              : currentConversion.from.cryptoAmount.currency,
        },
        currencyInfoInWallet:
          type === 'from'
            ? selectedWallet
            : currentConversion.from.currencyInfoInWallet,
      },
      to: {
        fiatAmount: {
          amount: '0',
          currency: (displayCurrency?.isin as FiatCode) || 'USD',
        },
        cryptoAmount: {
          amount: '0',
          currency:
            type === 'to'
              ? cryptoCurrencyCode
              : currentConversion.to.cryptoAmount.currency,
        },
        currencyInfoInWallet:
          type === 'to'
            ? selectedWallet
            : currentConversion.to.currencyInfoInWallet,
      },
    })
  }

  const handleToggleFiatMode = () => {
    setIsFiatMode(!isFiatMode)
  }

  return {
    handleInputChange,
    handleSwapCurrencies,
    handleSelectCryptoFromBottomSheet,
    isFiatMode,
    handleInputChangeInFiatMode,
    handleToggleFiatMode,
    isPriceLoading: isFromPriceLoading || isToPriceLoading,
  }
}
